import { Fragment } from 'react';

/**
 * A country or heritage name that is allowed to wrap.
 *
 * "Black American/Soulaan" has no spaces around its slash, so the browser reads
 * it as one unbreakable word and lets it run off the right edge of its card
 * rather than wrapping. The same is true of any future compound name.
 *
 * Two defences, because they fail differently:
 *
 *   1. A <wbr> after each "/" — an explicit break OPPORTUNITY. The browser
 *      wraps there only if it needs to, so short names are untouched and the
 *      slash stays on the first line where it reads naturally.
 *   2. overflowWrap: 'anywhere' as a backstop, so a long name with no slash at
 *      all still wraps instead of overflowing.
 *
 * The stored name is never modified: this is a rendering concern, and the
 * stored string is what drives count lookups elsewhere.
 *
 * The style is inline rather than a utility class on purpose. src/index.css is
 * committed pre-compiled Tailwind, so a class that is not already in that
 * bundle silently does nothing — exactly the trap that would make this look
 * fixed while still overflowing.
 */
export function CountryName({
  name,
  className,
  style,
  as: Tag = 'span',
}: {
  name: string;
  className?: string;
  style?: React.CSSProperties;
  /** Element to render as, so callers keep their heading semantics. */
  as?: 'span' | 'h3' | 'p' | 'div';
}) {
  const parts = String(name ?? '').split('/');

  return (
    <Tag
      className={className}
      style={{ overflowWrap: 'anywhere', ...style }}
      // The accessible name stays the real one: <wbr> inserts no character,
      // but keeping this explicit means a future change here cannot alter how
      // the name is announced.
      title={name}
    >
      {parts.map((part, i) => (
        <Fragment key={i}>
          {part}
          {i < parts.length - 1 && (
            <>
              {'/'}
              <wbr />
            </>
          )}
        </Fragment>
      ))}
    </Tag>
  );
}
